import { cn } from "@gettactic/helpers/src/css/cn";
import { MapIcon, UploadIcon, XIcon } from "lucide-react";
import React, { useEffect, useId, useMemo } from "react";
import { Button } from "../../primitives/button";
import { FormControlWrapper } from "../FormControlWrapper";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  FloorPlanUploadInputProps,
  FloorPlanUploadInputPropsWithFormControl
} from "./FloorPlanUploadInput.types";

const FloorPlanUploadInputCore: React.FC<FloorPlanUploadInputProps> = ({
  value,
  onChange,
  previewUrl: externalPreviewUrl,
  uploadLabel = "Upload SVG",
  changeLabel = "Change",
  removeLabel = "Remove",
  disabled = false,
  className,
  placeholder = "Drag & drop an SVG file here, or click to browse"
}) => {
  const inputId = useId();

  const previewUrl = useMemo(() => {
    if (value) {
      return URL.createObjectURL(value);
    }
    return externalPreviewUrl;
  }, [value, externalPreviewUrl]);

  useEffect(() => {
    return () => {
      if (value) {
        URL.revokeObjectURL(URL.createObjectURL(value));
      }
    };
  }, [value]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange?.(file);
    }
    e.target.value = "";
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file && file.type === "image/svg+xml") {
      onChange?.(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const hasFile = !!value || !!externalPreviewUrl;

  return (
    <div className={cn("w-full", className)}>
      <div
        onClick={() => !disabled && document.getElementById(inputId)?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={cn(
          "relative border-2 border-dashed rounded-xl p-4 text-center transition-all",
          disabled
            ? "pointer-events-none opacity-50 cursor-not-allowed bg-neutral-100 border-neutral-300"
            : "cursor-pointer hover:bg-neutral-50 bg-white border-neutral-300",
          hasFile && "border-solid border-accent bg-accent/5"
        )}
      >
        <input
          id={inputId}
          type="file"
          accept="image/svg+xml"
          className="hidden"
          disabled={disabled}
          onChange={handleFileSelect}
        />

        {hasFile ? (
          <div className="flex items-center gap-4">
            {previewUrl && (
              <div className="flex-shrink-0 h-16 w-16 rounded-lg border border-neutral-200 bg-white p-1 overflow-hidden">
                <img
                  src={previewUrl}
                  alt="Floor plan preview"
                  className="h-full w-full object-contain"
                />
              </div>
            )}
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-neutral-700 truncate">
                {value?.name || "Floor plan uploaded"}
              </p>
              <p className="text-xs text-neutral-500">
                {value ? `${(value.size / 1024).toFixed(1)} KB` : "SVG file"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById(inputId)?.click();
                }}
              >
                {changeLabel}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={handleRemove}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-4">
            <div className="flex justify-center mb-3">
              <div className="rounded-full bg-neutral-100 p-3">
                <MapIcon className="h-6 w-6 text-neutral-400" />
              </div>
            </div>
            <p className="text-sm text-neutral-600 mb-2">{placeholder}</p>
            <p className="text-xs text-neutral-400">SVG files only</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              className="mt-3"
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById(inputId)?.click();
              }}
            >
              <UploadIcon className="h-4 w-4 mr-2" />
              {uploadLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const FloorPlanUploadInput: React.FC<
  FloorPlanUploadInputPropsWithFormControl
> = (props) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <FormControlWrapper variant="unstyled" {...wrapperProps}>
      <FloorPlanUploadInputCore {...inputProps} />
    </FormControlWrapper>
  );
};

FloorPlanUploadInput.displayName = "FloorPlanUploadInput";
